import React, { useRef, useEffect, useState } from 'react';

import { Col, Container, Row, Spinner } from 'react-bootstrap';
import { GalleryItem } from '../Career/types';
import DOMPurify from "dompurify";
// components
import { Post2 } from 'components/blog';
import ContactForm from './ContactPost';
import { Helmet } from 'react-helmet-async';
import { getAllBlogs } from 'helpers/api/BlogService';
import { Post1 } from 'components/blog';
import BlogImage2 from 'assets/images/hero/New folder/BlogImage2.png';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

type PostContentProps = {
    gallery: GalleryItem[];
    post: Post2 | null;
};

interface BlogPost {
    id: number;
    blogTitle: string;
    shortDescription: string;
    status: number;
    blogTags: string;
    blogImage: string;
    createdOn: string;
    metaKeywords: string;
    slug: string;

}


const PostContent = ({ gallery, post }: PostContentProps) => {
    const sanitizeAndFixHeadings = (html: string) => {
        let clean = DOMPurify.sanitize(html);

        clean = clean
            .replace(/<h1>/g, "<h2>")
            .replace(/<\/h1>/g, "</h2>")
            .replace(/\?\s/g, "• ");

        return clean;
    };

    const formatTitleForURL = (title?: string) =>
        title
            ? title
                .toLowerCase()
                .replace(/[^a-z0-9\s-]/g, "")
                .replace(/\s+/g, "-")
                .trim()
            : "";



    const sanitizedContent = sanitizeAndFixHeadings(post?.longDescription || '');
    const leftColRef = useRef<HTMLDivElement>(null);
    const rightColRef = useRef<HTMLDivElement>(null);
    const rightInnerRef = useRef<HTMLDivElement>(null);
    const [stackBelow, setStackBelow] = useState(false);
    const [leftTaller, setLeftTaller] = useState(false);
    const [formCompleted, setFormCompleted] = useState(false);
    const [collapseRight, setCollapseRight] = useState(false);
    const [allBlogList, setAllBlogList] = useState<Post1[]>([]);


    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                const response = await getAllBlogs();


                if (response?.success && Array.isArray(response.result)) {
                    const activeBlogs = response.result.filter((blog: any) => blog.status === 1);

                    const transformedData: Post1[] = activeBlogs.map((blog: any) => ({
                        id: blog.id,
                        title: blog.blogTitle,
                        description: blog.shortDescription,
                        image: `${blog.blogImage}`,
                        tag: blog.blogTags,
                        createdOn: blog.createdOn,
                        metaKeywords: blog.metaKeywords,
                        slug: blog.slug
                    }));

                    const relatedBlogs = transformedData.filter(item => item.id !== post?.id);
                    setAllBlogList(relatedBlogs);
                } else {
                    console.error('Invalid response format:', response);
                }
            } catch (error) {
                console.error('Failed to fetch blogs:', error);
            }
        };

        fetchBlogs();
    }, []);



    useEffect(() => {
        if (!collapseRight) return;
        const el = rightInnerRef.current || rightColRef.current;
        if (!el) {
            setFormCompleted(true);
            return;
        }

        const onTransitionEnd = (e: TransitionEvent) => {
            if ((e as any).propertyName === 'max-height' || (e as any).propertyName === 'height') {
                setFormCompleted(true);
                setCollapseRight(false);
            }
        };

        el.addEventListener('transitionend', onTransitionEnd as any);
        const t = setTimeout(() => {
            setFormCompleted(true);
            setCollapseRight(false);
        }, 700);

        return () => {
            clearTimeout(t);
            el.removeEventListener('transitionend', onTransitionEnd as any);
        };
    }, [collapseRight]);


    useEffect(() => {
        if (formCompleted) {
            setStackBelow(false);
            return;
        }

        const measure = () => {
            const leftH = leftColRef.current?.offsetHeight || 0;
            const rightH = rightColRef.current?.offsetHeight || 0;
            setStackBelow(rightH > leftH);
            setLeftTaller(leftH > rightH + 80);
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [post, formCompleted]);

    if (!post) {
        return (
            <div style={{ padding: "40px", textAlign: "center" }}>
                <h1>Loading Blog...</h1>
                <p>Please wait while we load the content.</p>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>{`${post?.title} | Sales Trackify CRM`}</title>
                <meta name="description" content={post?.shortDescription} />
                <meta name="keywords" content={post?.metaKeywords || post?.blogTags} />

                <link
                    rel="canonical"
                    href={`https://www.salestrackify.com/blog/${formatTitleForURL(post?.title)}`}
                />

                <meta property="og:title" content={post?.title} />
                <meta property="og:description" content={post?.shortDescription} />
                <meta property="og:image" content={post?.blogImage} />
            </Helmet>

            <section className="position-relative pb-5">
                <Container fluid={formCompleted}>
                    <Row className={`mb-5 ${!formCompleted ? 'two-column-row' : ''}`} style={{ alignItems: 'flex-start' }}>
                        <Col
                            lg={formCompleted ? 12 : 7}
                            ref={leftColRef}
                            className={`${formCompleted ? 'blog-fullwidth' : ''} left-column`}
                        >
                            <div className={!formCompleted ? 'left-scroll' : ''}>
                                <p
                                    className="mb-4 mt-3"
                                    style={{
                                        fontSize: '1.08rem',
                                        lineHeight: 1.8,
                                        color: '#4b5563',
                                        borderLeft: '3px solid #26A2AB',
                                        paddingLeft: '16px',
                                    }}
                                >
                                    {post?.shortDescription}
                                </p>

                                {(!stackBelow || formCompleted) && post.blogImage && post.blogImage.trim() !== '' && (
                                    <figure className="figure text-center mb-4 mt-3" style={{ width: '100%' }}>
                                        <img
                                            src={post.blogImage}
                                            alt="Blog"
                                            className="figure-img img-fluid"
                                            style={{
                                                maxWidth: '100%',
                                                height: 'auto',
                                                maxHeight: '420px',
                                                objectFit: 'cover',
                                                borderRadius: '12px',
                                            }}
                                        />
                                    </figure>
                                )}


                                <div className="blog-content mt-4">
                                    <div
                                        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                                    />
                                </div>
                            </div>
                        </Col>

                        {!formCompleted && (
                            <Col lg={5} ref={rightColRef} className="right-column">

                                <div>
                                    <div
                                        ref={rightInnerRef}
                                        className={
                                            collapseRight
                                                ? 'collapsing-right closing border-wrapper sticky-form'
                                                : 'collapsing-right border-wrapper sticky-form'
                                        }
                                    >
                                        <div className="border border-primary">
                                            <ContactForm />
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        )}
                    </Row>


                    {stackBelow && post.blogImage && (
                        <Row className="mb-5">
                            <Col lg={12}>
                                <figure className="figure text-center mb-4">
                                    <img
                                        src={post.blogImage}
                                        alt="Blog"
                                        className="figure-img img-fluid rounded shadow"
                                        style={{
                                            maxWidth: '100%',
                                            height: 'auto',
                                            maxHeight: '400px',
                                            objectFit: 'cover'
                                        }}
                                    />
                                </figure>
                            </Col>
                        </Row>
                    )}
                    {/* 
                    {stackBelow && secondPart && (
                        <Row>
                            <Col lg={12}>
                                <div className="mt-4">
                                    <div
                                        dangerouslySetInnerHTML={{ __html: sanitizedSecondPart }}
                                    />
                                </div>
                            </Col>
                        </Row>
                    )} */}
                </Container>
            </section>

            {/* ===================== RELATED BLOGS SECTION ===================== */}
            {allBlogList.length > 0 && (
                <section style={{ padding: '48px 0 56px', background: '#fafbfc', borderTop: '1px solid #f0f0f0' }}>
                    <Container>
                        <div className="d-flex align-items-center mb-4" style={{ gap: '10px' }}>
                            <span style={{ width: '32px', height: '3px', background: '#26A2AB', display: 'inline-block', borderRadius: '2px' }} />
                            <h2 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#1a1a2e', margin: 0 }}>You might also like</h2>
                        </div>
                        <Row>
                            {allBlogList.slice(0, 3).map((blog) => (
                                <Col xs={12} sm={6} md={4} className="mb-4" key={blog.id}>
                                    <div
                                        className="related-card h-100 d-flex flex-column"
                                        style={{
                                            background: '#fff',
                                            borderRadius: '12px',
                                            border: '1px solid #eaeaea',
                                            overflow: 'hidden',
                                            transition: 'box-shadow 0.25s ease, transform 0.25s ease',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.boxShadow = '0 8px 28px rgba(0,0,0,0.08)';
                                            e.currentTarget.style.transform = 'translateY(-3px)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.boxShadow = 'none';
                                            e.currentTarget.style.transform = 'translateY(0)';
                                        }}
                                    >
                                        <a href={`/${blog.slug}`}

                                            className="d-block position-relative"
                                            style={{ aspectRatio: '16/10', overflow: 'hidden' }}
                                        >
                                            <img
                                                src={blog.image || BlogImage2}
                                                alt={blog.slug}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.3s ease' }}
                                                onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.04)'; }}
                                                onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                                            />
                                            {blog.tag && (
                                                <span
                                                    style={{
                                                        position: 'absolute',
                                                        top: '10px',
                                                        left: '10px',
                                                        background: '#26A2AB',
                                                        color: '#fff',
                                                        padding: '3px 10px',
                                                        fontSize: '0.68rem',
                                                        fontWeight: 600,
                                                        borderRadius: '4px',
                                                        letterSpacing: '0.5px',
                                                        textTransform: 'uppercase',
                                                    }}
                                                >
                                                    {blog.tag}
                                                </span>
                                            )}
                                        </a>

                                        <div className="d-flex flex-column flex-grow-1 p-3">
                                            <small style={{ color: '#9ca3af', fontSize: '0.78rem' }}>
                                                {new Date(blog.createdOn).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                            </small>

                                            <h5
                                                className="mt-2 mb-2"
                                                style={{
                                                    fontSize: '0.95rem',
                                                    fontWeight: 700,
                                                    lineHeight: 1.4,
                                                    color: '#1a1a2e',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                {blog.title}
                                            </h5>

                                            <p
                                                className="flex-grow-1 mb-0"
                                                style={{
                                                    fontSize: '0.84rem',
                                                    color: '#6b7280',
                                                    lineHeight: 1.6,
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                {blog.description}
                                            </p>

                                            <div className="mt-auto pt-3">
                                                <a href={`/${blog.slug}`}
                                                    // state={{ id: blog.id }}
                                                    className="d-inline-flex align-items-center text-decoration-none"
                                                    style={{ fontSize: '0.84rem', fontWeight: 600, color: '#26A2AB', gap: '4px', transition: 'gap 0.2s' }}
                                                    onMouseEnter={(e) => { e.currentTarget.style.gap = '8px'; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.gap = '4px'; }}
                                                >
                                                    Read More <ArrowRight size={14} />
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </Container>
                </section>
            )}

            {/* extra styles for post layout adjustments */}
            <style>{`
                .two-column-content {
                    column-count: 2;
                    column-gap: 2rem;
                }

                .blog-fullwidth {
                    width: 100%;
                }

                .border-wrapper {
                    display: block;
                }

                .collapsing-right {
                    max-height: 2000px;
                    transition: max-height 0.45s ease, opacity 0.35s ease, padding 0.35s ease;
                    opacity: 1;
                }

                .sticky-form {
                    position: sticky;
                    top: 100px;
                    height: fit-content;
                }

                .collapsing-right.closing {
                    max-height: 0 !important;
                    opacity: 0;
                    padding-top: 0 !important;
                    padding-bottom: 0 !important;
                }

                /* Blog content typography — clean editorial style */
                .blog-content {
                    font-size: 1.05rem;
                    color: #374151;
                }

                .blog-content p {
                    font-size: 1.05rem;
                    line-height: 1.85;
                    margin-bottom: 1.2rem;
                    color: #374151;
                }

                .blog-content h1,
                .blog-content h2,
                .blog-content h3,
                .blog-content h4,
                .blog-content h5,
                .blog-content h6 {
                    color: #1a1a2e;
                    font-weight: 700;
                    margin-top: 2rem;
                    margin-bottom: 0.6rem;
                    line-height: 1.35;
                }

                .blog-content h2 {
                    font-size: 1.45rem;
                }

                .blog-content h3 {
                    font-size: 1.2rem;
                }

                .blog-content ul,
                .blog-content ol {
                    padding-left: 1.4rem;
                    margin-bottom: 1.2rem;
                }

                .blog-content li {
                    list-style: disc;
                    margin-bottom: 0.4rem;
                    line-height: 1.75;
                }

                .blog-content strong {
                    font-weight: 700;
                    color: #1a1a2e;
                }

                .blog-content a {
                    color: #26A2AB;
                    text-decoration: underline;
                    text-underline-offset: 2px;
                }

                .blog-content a:hover {
                    color: #24347D;
                }

                .blog-content blockquote {
                    border-left: 3px solid #26A2AB;
                    padding: 12px 20px;
                    margin: 1.5rem 0;
                    background: #f9fafb;
                    border-radius: 0 6px 6px 0;
                    color: #4b5563;
                    font-style: italic;
                }

                .blog-content img {
                    border-radius: 10px;
                    max-width: 100%;
                    height: auto;
                }

                .blog-content table {
                    width: 100%;
                    border-collapse: collapse;
                    margin: 1.5rem 0;
                }

                .blog-content th,
                .blog-content td {
                    padding: 10px 14px;
                    border: 1px solid #e5e7eb;
                    text-align: left;
                }

                .blog-content th {
                    background: #f3f4f6;
                    font-weight: 600;
                }
            `}</style>

        </>
    );
};

export default PostContent;